/*
  Testimonial slider
  ------------------
  Custom element used by sections/testimonial-slider.liquid. No globals are created and
  no third-party slider is involved: everything lives on the element instance so the
  theme editor can load, unload and re-render the section as many times as it likes.

  Infinite loop: the last `perView` slides are cloned in front of the track and the first
  `perView` slides are cloned after it. The index is allowed to run one step past either
  end of the real range; when the transition finishes it is shifted back by the number of
  real slides and the transform is reapplied with the transition switched off, so the wrap
  is never visible.
*/
(function () {
  var TAG = 'testimonial-slider';

  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get(TAG)) return;

  var REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

  function TestimonialSliderFactory() {
    return class TestimonialSlider extends HTMLElement {
      constructor() {
        super();

        this.index = 0;
        this.step = 0;
        this.perView = 1;
        this.cloneCount = 0;
        this.slides = [];
        this.dots = [];
        this.active = false;
        this.built = false;
        this.isSliding = false;
        this.timer = null;
        this.settleTimer = null;
        this.pausedBy = {
          hover: false,
          focus: false,
          hidden: false,
          offscreen: false,
          editor: false,
        };

        this.handlePrev = this.handlePrev.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleDot = this.handleDot.bind(this);
        this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
        this.settle = this.settle.bind(this);
        this.handlePointerEnter = this.handlePointerEnter.bind(this);
        this.handlePointerLeave = this.handlePointerLeave.bind(this);
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.handleMotionChange = this.handleMotionChange.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleIntersect = this.handleIntersect.bind(this);
        this.handleSectionLoad = this.handleSectionLoad.bind(this);
        this.handleSectionUnload = this.handleSectionUnload.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
      }

      /* ---------- lifecycle ---------- */

      connectedCallback() {
        this.viewport = this.querySelector('[data-ts-viewport]');
        this.track = this.querySelector('[data-ts-track]');
        if (!this.viewport || !this.track) return;

        this.slides = Array.prototype.filter.call(this.track.children, function (child) {
          return child.hasAttribute('data-ts-slide') && !child.hasAttribute('data-ts-clone');
        });
        if (!this.slides.length) return;

        this.prevButton = this.querySelector('[data-ts-prev]');
        this.nextButton = this.querySelector('[data-ts-next]');
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-ts-dot]'));

        if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrev);
        if (this.nextButton) this.nextButton.addEventListener('click', this.handleNext);
        this.dots.forEach(
          function (dot) {
            dot.addEventListener('click', this.handleDot);
          }.bind(this)
        );

        this.track.addEventListener('transitionend', this.handleTransitionEnd);
        this.addEventListener('mouseenter', this.handlePointerEnter);
        this.addEventListener('mouseleave', this.handlePointerLeave);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        if (window.matchMedia) {
          this.motionQuery = window.matchMedia(REDUCED_MOTION);
          if (this.motionQuery.addEventListener) {
            this.motionQuery.addEventListener('change', this.handleMotionChange);
          } else if (this.motionQuery.addListener) {
            this.motionQuery.addListener(this.handleMotionChange);
          }
        }

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.handleResize);
          this.resizeObserver.observe(this.viewport);
        } else {
          window.addEventListener('resize', this.handleResize);
        }

        if ('IntersectionObserver' in window) {
          this.intersectionObserver = new IntersectionObserver(this.handleIntersect, { threshold: 0 });
          this.intersectionObserver.observe(this);
        }

        document.addEventListener('shopify:section:load', this.handleSectionLoad);
        document.addEventListener('shopify:section:unload', this.handleSectionUnload);
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);

        this.pausedBy.hidden = document.hidden === true;
        this.rebuild();
      }

      disconnectedCallback() {
        this.destroy();
      }

      destroy() {
        this.stopAutoplay();
        this.clearSettleFallback();
        this.isSliding = false;

        if (this.prevButton) this.prevButton.removeEventListener('click', this.handlePrev);
        if (this.nextButton) this.nextButton.removeEventListener('click', this.handleNext);
        this.dots.forEach(
          function (dot) {
            dot.removeEventListener('click', this.handleDot);
          }.bind(this)
        );

        if (this.track) this.track.removeEventListener('transitionend', this.handleTransitionEnd);
        this.removeEventListener('mouseenter', this.handlePointerEnter);
        this.removeEventListener('mouseleave', this.handlePointerLeave);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);

        if (this.motionQuery) {
          if (this.motionQuery.removeEventListener) {
            this.motionQuery.removeEventListener('change', this.handleMotionChange);
          } else if (this.motionQuery.removeListener) {
            this.motionQuery.removeListener(this.handleMotionChange);
          }
          this.motionQuery = null;
        }

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        } else {
          window.removeEventListener('resize', this.handleResize);
        }

        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
          this.intersectionObserver = null;
        }

        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
        document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);

        this.removeClones();
        if (this.track) {
          this.track.style.transitionDuration = '';
          this.track.style.transform = '';
        }
        this.built = false;
      }

      /* ---------- settings ---------- */

      get autoplayEnabled() {
        return this.dataset.autoplay === 'true';
      }

      get autoplayDelay() {
        var seconds = parseFloat(this.dataset.autoplaySpeed);
        if (!seconds || seconds < 1) seconds = 5;
        return seconds * 1000;
      }

      get reducedMotion() {
        return this.motionQuery ? this.motionQuery.matches : false;
      }

      /* Reads the cards-per-view value straight out of the computed custom property, so
         CSS media queries stay the single source of truth for the breakpoints. */
      readPerView() {
        var raw = window.getComputedStyle(this.track).getPropertyValue('--ts-per-view');
        var value = parseInt(raw, 10);
        if (!value || value < 1) value = 1;
        return Math.min(value, 4);
      }

      /* ---------- build ---------- */

      rebuild() {
        if (!this.track || !this.slides.length) return;

        var perView = this.readPerView();
        var needsClones = perView !== this.perView || !this.built;

        this.perView = perView;
        this.active = this.slides.length > perView;

        if (needsClones) this.buildClones();
        this.built = true;

        if (!this.active) this.index = 0;

        this.toggleAttribute('data-ts-static', !this.active);
        this.measure();
        this.render(false);
        this.updateAutoplay();
      }

      removeClones() {
        if (!this.track) return;
        var clones = this.track.querySelectorAll('[data-ts-clone]');
        Array.prototype.forEach.call(clones, function (clone) {
          clone.parentNode.removeChild(clone);
        });
        this.cloneCount = 0;
      }

      buildClones() {
        this.removeClones();
        if (!this.active) return;

        var count = Math.min(this.perView, this.slides.length);
        var i;

        for (i = 0; i < count; i++) {
          // Leading clones mirror the tail, inserted in reverse so order is preserved.
          this.track.insertBefore(this.makeClone(this.slides[this.slides.length - 1 - i]), this.track.firstChild);
          this.track.appendChild(this.makeClone(this.slides[i]));
        }

        this.cloneCount = count;
      }

      makeClone(slide) {
        var clone = slide.cloneNode(true);
        clone.setAttribute('data-ts-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        // Strip the theme editor hooks so a clone can never be mistaken for a real block.
        Array.prototype.slice.call(clone.attributes).forEach(function (attribute) {
          if (attribute.name.indexOf('data-shopify-editor') === 0) {
            clone.removeAttribute(attribute.name);
          }
        });
        return clone;
      }

      measure() {
        var slide = this.slides[0];
        if (!slide) return;

        var styles = window.getComputedStyle(this.track);
        var gap = parseFloat(styles.columnGap || styles.gap || '0');
        if (isNaN(gap)) gap = 0;

        this.step = slide.getBoundingClientRect().width + gap;
      }

      /* ---------- movement ---------- */

      render(animate) {
        if (!this.track) return false;

        var shouldAnimate = animate && !this.reducedMotion && this.step > 0;
        var offset = this.active ? -(this.index + this.cloneCount) * this.step : 0;
        var transform = 'translate3d(' + offset + 'px, 0, 0)';

        if (shouldAnimate) {
          this.isSliding = true;
          this.armSettleFallback();
          this.track.style.transform = transform;
        } else {
          this.isSliding = false;
          this.clearSettleFallback();
          this.track.style.transitionDuration = '0ms';
          this.track.style.transform = transform;
          // Force a reflow so the jump is committed before transitions come back.
          this.track.getBoundingClientRect();
          this.track.style.transitionDuration = '';
        }

        this.updateDots();
        return shouldAnimate;
      }

      /* transitionend is the normal settle trigger; this covers the cases where it never
         fires (a zero-length transition, a tab hidden mid-slide, an unchanged transform). */
      armSettleFallback() {
        this.clearSettleFallback();
        var duration = parseFloat(window.getComputedStyle(this.track).transitionDuration) || 0.5;
        this.settleTimer = window.setTimeout(this.settle, duration * 1000 + 120);
      }

      clearSettleFallback() {
        if (!this.settleTimer) return;
        window.clearTimeout(this.settleTimer);
        this.settleTimer = null;
      }

      settle() {
        this.clearSettleFallback();
        this.isSliding = false;
        this.normalize();
      }

      /* Pulls an out-of-range index back into the real range without any visible move. */
      normalize() {
        var total = this.slides.length;
        if (this.index >= total) {
          this.index -= total;
        } else if (this.index < 0) {
          this.index += total;
        } else {
          return;
        }
        this.render(false);
      }

      move(delta) {
        if (!this.active || this.isSliding) return;
        this.index += delta;
        var animated = this.render(true);
        if (!animated) this.normalize();
      }

      goToIndex(target) {
        if (!this.active || this.isSliding) return;
        if (target === this.realIndex()) return;
        this.index = target;
        this.render(true);
      }

      realIndex() {
        var total = this.slides.length;
        return ((this.index % total) + total) % total;
      }

      updateDots() {
        if (!this.dots.length) return;
        var current = this.realIndex();
        this.dots.forEach(function (dot, position) {
          var dotIndex = parseInt(dot.dataset.tsDot, 10);
          if (isNaN(dotIndex)) dotIndex = position;
          if (dotIndex === current) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      /* ---------- autoplay ---------- */

      get shouldAutoplay() {
        if (!this.autoplayEnabled || !this.active || this.reducedMotion) return false;
        var flags = this.pausedBy;
        return !(flags.hover || flags.focus || flags.hidden || flags.offscreen || flags.editor);
      }

      updateAutoplay() {
        if (this.shouldAutoplay) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      }

      startAutoplay() {
        if (this.timer) return;
        this.timer = window.setInterval(
          function () {
            this.move(1);
          }.bind(this),
          this.autoplayDelay
        );
      }

      stopAutoplay() {
        if (!this.timer) return;
        window.clearInterval(this.timer);
        this.timer = null;
      }

      pause(reason) {
        this.pausedBy[reason] = true;
        this.updateAutoplay();
      }

      resume(reason) {
        this.pausedBy[reason] = false;
        this.updateAutoplay();
      }

      /* ---------- handlers ---------- */

      handlePrev() {
        this.move(-1);
      }

      handleNext() {
        this.move(1);
      }

      handleDot(event) {
        var target = parseInt(event.currentTarget.dataset.tsDot, 10);
        if (isNaN(target)) return;
        this.goToIndex(target);
      }

      handleTransitionEnd(event) {
        if (event.target !== this.track || event.propertyName !== 'transform') return;
        this.settle();
      }

      handlePointerEnter() {
        this.pause('hover');
      }

      handlePointerLeave() {
        this.resume('hover');
      }

      handleFocusIn() {
        this.pause('focus');
      }

      handleFocusOut(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.resume('focus');
      }

      handleVisibilityChange() {
        if (document.hidden) {
          this.pause('hidden');
        } else {
          this.resume('hidden');
        }
      }

      handleMotionChange() {
        this.render(false);
        this.normalize();
        this.updateAutoplay();
      }

      handleResize() {
        this.rebuild();
      }

      handleIntersect(entries) {
        var entry = entries[entries.length - 1];
        if (!entry) return;
        if (entry.isIntersecting) {
          this.resume('offscreen');
        } else {
          this.pause('offscreen');
        }
      }

      /* ---------- theme editor ---------- */

      handleSectionLoad(event) {
        if (!event.target.contains(this)) return;
        this.built = false;
        this.rebuild();
      }

      handleSectionUnload(event) {
        if (!event.target.contains(this)) return;
        this.destroy();
      }

      handleBlockSelect(event) {
        if (!this.contains(event.target)) return;
        var slide = event.target.closest('[data-ts-slide]');
        if (slide) {
          var position = this.slides.indexOf(slide);
          if (position > -1) this.goToIndex(position);
        }
        this.pause('editor');
      }

      handleBlockDeselect(event) {
        if (!this.contains(event.target)) return;
        this.resume('editor');
      }
    };
  }

  customElements.define(TAG, TestimonialSliderFactory());
})();
